import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { CUSTOM_VISUAL_DEFAULTS } from "../customVisualOwnership";

const field = (path, type, label, defaultValue, extra = {}) => ({ path, type, label, defaultValue, explicitVisual: true, uiGroup: "design", ...extra });
const color = (path, label, defaultValue) => field(path, SEGMENT_FIELD_TYPES.COLOR, label, defaultValue, { maxLength: 100 });
const number = (path, label, defaultValue, min, max, step = 1) => field(path, SEGMENT_FIELD_TYPES.NUMBER, label, defaultValue, { limits: { min, max, step } });
const select = (path, label, defaultValue, options) => field(path, SEGMENT_FIELD_TYPES.SELECT, label, defaultValue, { options });

function shadowFields(prefix, label, defaults) {
  return [
    color(`${prefix}.shadowColor`, `${label} · color de sombra`, defaults.shadowColor),
    number(`${prefix}.shadowOpacity`, `${label} · opacidad de sombra`, defaults.shadowOpacity, 0, 1, 0.05),
    number(`${prefix}.shadowX`, `${label} · sombra X`, defaults.shadowX, -120, 120),
    number(`${prefix}.shadowY`, `${label} · sombra Y`, defaults.shadowY, -120, 120),
    number(`${prefix}.shadowBlur`, `${label} · desenfoque`, defaults.shadowBlur, 0, 220),
    number(`${prefix}.shadowSpread`, `${label} · expansión`, defaults.shadowSpread, -100, 100),
  ];
}

function mediaFields(prefix, label, defaults, { heights = false, objectFit = false } = {}) {
  return [
    color(`${prefix}.backgroundColor`, `${label} · superficie`, defaults.backgroundColor),
    color(`${prefix}.borderColor`, `${label} · borde`, defaults.borderColor),
    number(`${prefix}.borderWidth`, `${label} · grosor de borde`, defaults.borderWidth, 0, 24),
    number(`${prefix}.borderRadius`, `${label} · redondeado`, defaults.borderRadius, 0, 320),
    ...shadowFields(prefix, label, defaults),
    ...(heights ? [
      number(`${prefix}.heightMobile`, `${label} · alto mobile`, defaults.heightMobile, 120, 1400),
      number(`${prefix}.heightDesktop`, `${label} · alto desktop`, defaults.heightDesktop, 120, 1600),
    ] : []),
    ...(objectFit ? [select(`${prefix}.objectFit`, `${label} · ajuste`, defaults.objectFit, [{ value: "cover", label: "Cubrir" }, { value: "contain", label: "Contener" }])] : []),
  ];
}

export function customSectionVisualProperties(root) {
  const style = `${root}.style`;
  const custom = `${style}.custom`;
  return [
    color(`${style}.background.color`, "Fondo · color", CUSTOM_VISUAL_DEFAULTS.background.color),
    field(`${style}.background.imageUrl`, SEGMENT_FIELD_TYPES.IMAGE, "Fondo · imagen", CUSTOM_VISUAL_DEFAULTS.background.imageUrl),
    select(`${style}.background.imagePosition`, "Fondo · posición", CUSTOM_VISUAL_DEFAULTS.background.imagePosition, ["center","top","bottom","left","right"].map((value) => ({ value, label: value }))),
    select(`${style}.background.imageSize`, "Fondo · ajuste", CUSTOM_VISUAL_DEFAULTS.background.imageSize, [{ value: "cover", label: "Cubrir" }, { value: "contain", label: "Contener" }, { value: "auto", label: "Tamaño original" }]),
    number(`${custom}.layout.spacingScale`, "Espaciado de sección", CUSTOM_VISUAL_DEFAULTS.layout.spacingScale, 0.5, 1.5, 0.02),
    select(`${custom}.layout.heightMode`, "Altura de sección", CUSTOM_VISUAL_DEFAULTS.layout.heightMode, [{ value: "auto", label: "Automática" }, { value: "fixed", label: "Altura personalizada" }]),
    number(`${custom}.layout.heightDesktop`, "Alto escritorio (px)", CUSTOM_VISUAL_DEFAULTS.layout.heightDesktop, 220, 1600, 20),
    number(`${custom}.layout.heightMobile`, "Alto celular (px)", CUSTOM_VISUAL_DEFAULTS.layout.heightMobile, 180, 1200, 20),

    color(`${custom}.eyebrow.color`, "Texto superior · color", CUSTOM_VISUAL_DEFAULTS.eyebrow.color),
    number(`${custom}.eyebrow.fontWeight`, "Texto superior · peso", CUSTOM_VISUAL_DEFAULTS.eyebrow.fontWeight, 100, 1000, 50),
    number(`${custom}.eyebrow.letterSpacing`, "Texto superior · espaciado", CUSTOM_VISUAL_DEFAULTS.eyebrow.letterSpacing, -0.2, 0.5, 0.01),

    color(`${custom}.title.color`, "Título · color", CUSTOM_VISUAL_DEFAULTS.title.color),
    number(`${custom}.title.fontScale`, "Título · escala", CUSTOM_VISUAL_DEFAULTS.title.fontScale, 0.6, 1.6, 0.02),
    number(`${custom}.title.fontWeight`, "Título · peso", CUSTOM_VISUAL_DEFAULTS.title.fontWeight, 100, 1000, 50),

    color(`${custom}.description.color`, "Descripción · color", CUSTOM_VISUAL_DEFAULTS.description.color),
    number(`${custom}.description.lineHeight`, "Descripción · interlineado", CUSTOM_VISUAL_DEFAULTS.description.lineHeight, 0.8, 3, 0.05),

    ...mediaFields(`${custom}.image`, "Imagen", CUSTOM_VISUAL_DEFAULTS.image, { heights: true, objectFit: true }),

    color(`${custom}.cta.backgroundColor`, "CTA · fondo", CUSTOM_VISUAL_DEFAULTS.cta.backgroundColor),
    color(`${custom}.cta.textColor`, "CTA · texto", CUSTOM_VISUAL_DEFAULTS.cta.textColor),
    color(`${custom}.cta.hoverBackgroundColor`, "CTA · fondo hover", CUSTOM_VISUAL_DEFAULTS.cta.hoverBackgroundColor),
    color(`${custom}.cta.hoverTextColor`, "CTA · texto hover", CUSTOM_VISUAL_DEFAULTS.cta.hoverTextColor),
    color(`${custom}.cta.borderColor`, "CTA · borde", CUSTOM_VISUAL_DEFAULTS.cta.borderColor),
    number(`${custom}.cta.borderWidth`, "CTA · grosor de borde", CUSTOM_VISUAL_DEFAULTS.cta.borderWidth, 0, 16),
    number(`${custom}.cta.borderRadius`, "CTA · redondeado", CUSTOM_VISUAL_DEFAULTS.cta.borderRadius, 0, 999),
    ...shadowFields(`${custom}.cta`, "CTA", CUSTOM_VISUAL_DEFAULTS.cta),
    color(`${custom}.cta.focusColor`, "CTA · foco", CUSTOM_VISUAL_DEFAULTS.cta.focusColor),
    number(`${custom}.cta.fontWeight`, "CTA · peso del texto", CUSTOM_VISUAL_DEFAULTS.cta.fontWeight, 100, 1000, 50),

    ...mediaFields(`${custom}.youtube`, "YouTube", CUSTOM_VISUAL_DEFAULTS.youtube),
    ...mediaFields(`${custom}.calendly`, "Calendly", CUSTOM_VISUAL_DEFAULTS.calendly, { heights: true }),
  ];
}
